import debounce from 'lodash/debounce';
import { useClientUser } from '@/hooks';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { createContext, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { GET_ALL_ORDER_SHIPPERS, GET_ORDERS_INVENTORY, GET_ORDERS_INVENTORY_EXPORT } from './graphql/queries';
import { useTableColumns } from './columns';
import csvDownload from 'json-to-csv-export';
import {
    INVENTORY_CSV_COLUMNS,
    INVENTORY_EXPORT_LIMIT,
    INVENTORY_PAGE_SIZE,
    DELIVERY_TABS,
    PRIMARY_TABS,
} from './constants';
import { getInventoryCursor, getInventoryCursorWhere, processOrders, processPallets } from './utils';
import { PALLET_STATUS_LABELS } from '@/constants/manifestStatuses';
import { INSERT_CSV_EXPORT_TEMPLATE } from '@/graphql/mutations/csv_export_templates';
import { useExportTemplateOwnerId } from '../ExportCsvTemplateModal/useExportTemplateOwnerId';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { user_id } = useClientUser();
    const templateOwnerId = useExportTemplateOwnerId();

    const [modal, setModal] = useState(null);
    const [notification, setNotification] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    // Landing on Delivery preserves what every existing user already sees.
    const [primaryTab, setPrimaryTab] = useState(PRIMARY_TABS.DELIVERY);
    const [hasMore, setHasMore] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedSku, setSelectedSku] = useState(null);
    const [filters, setFilters] = useState([]);
    const [isPalletView, setIsPalletView] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [loadedSearch, setLoadedSearch] = useState('');
    const loadOrdersRequestId = useRef(0);
    const lastRequestedWhere = useRef(null);
    const reportInventoryError = useCallback((error, message) => {
        console.error(error);
        captureException(error);
        setNotification({
            severity: 'error',
            message,
        });
    }, []);
    const SEARCHABLE = [
        'order.order_number',
        'order.po_number',
        'order.dropoff_phone',
        'order.dropoff_name',
        'order.itemsByOrderId.sku',
        'order.itemsByOrderId.description',
        'order.itemsByOrderId.pallet.pallet_name',
        'order.order_shipper.business_name',
    ];

    const orderQuery = DELIVERY_TABS[tabIndex].orderQuery;
    const where = useMemo(() => {
        const query = [
            {
                _or: [
                    { carrier_id: { _eq: user_id } },
                    { shipper_id: { _eq: user_id } },
                    { warehouse_carrier_id: { _eq: user_id } },
                ],
            },
            { _or: [{ wh_events: { action: { _ilike: `START:AWAITING` } } }, { order_type: { _eq: 'will_call' } }] },
        ];
        let searchBuckets = {};

        if (search) {
            SEARCHABLE.forEach((attr) => {
                if (attr.includes('.')) {
                    const parts = attr.split('.');
                    return parts.reduceRight((acc, part, index) => {
                        if (index === parts.length - 1) {
                            const searchQuery = { [part]: { _ilike: `%${search.trim()}%` } };

                            const searchTermLevel = parts?.[index - 1];
                            if (searchTermLevel) {
                                if (!searchBuckets[searchTermLevel]) {
                                    searchBuckets[searchTermLevel] = [];
                                }
                                searchBuckets[searchTermLevel].push(searchQuery);
                            }

                            return searchQuery;
                        }
                        const searchQuery = { [part]: acc };
                        const searchTermLevel = parts?.[index - 1];
                        if (searchTermLevel) {
                            if (!searchBuckets[searchTermLevel]) {
                                searchBuckets[searchTermLevel] = [];
                            }
                            searchBuckets[searchTermLevel].push(searchQuery);
                        }
                        return searchQuery;
                    }, {});
                }
                return { [attr]: { _ilike: `%${search.trim()}%` } };
            });
        }

        if (orderQuery) {
            query.push(orderQuery);
        }

        return { _and: [...query, ...filters, { _or: searchBuckets.order }] };
    }, [search, filters, orderQuery, user_id]);

    const [getOrders] = useLazyQuery(GET_ORDERS_INVENTORY, {
        fetchPolicy: 'no-cache',
    });

    const [getExportData, { loading: exportOrdersLoading }] = useLazyQuery(GET_ORDERS_INVENTORY_EXPORT, {
        fetchPolicy: 'no-cache',
    });

    const { data: shipperData } = useQuery(GET_ALL_ORDER_SHIPPERS, {
        variables: {
            user_id: user_id,
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const [insertTemplate] = useMutation(INSERT_CSV_EXPORT_TEMPLATE, {
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'csv_export_templates' });
        },
        onError: (error) => {
            captureException(error);
            console.log(error.message);
        },
    });

    const shipperOptions = useMemo(() => {
        return Object.fromEntries(
            (shipperData?.orders || [])
                .map((o) => [o.shipper_id, o.order_shipper.business_name])
                .filter(([shipper_id]) => shipper_id && shipper_id !== user_id)
        );
    }, [shipperData]);

    // the sku pre-filter has to use the term these rows were fetched with, not what's been typed since
    const itemsBySku = useMemo(() => processOrders(orders, tabIndex, loadedSearch), [orders, tabIndex, loadedSearch]);

    const palletsBySku = useMemo(() => processPallets(itemsBySku, tabIndex), [itemsBySku, tabIndex]);

    const loadOrders = useCallback(
        async ({ requestId, requestedSearch, queryOptions }) => {
            try {
                if (requestId !== loadOrdersRequestId.current) {
                    return;
                }
                setOrders([]);
                const result = await getOrders(queryOptions);
                if (requestId !== loadOrdersRequestId.current) {
                    return;
                }
                if (result.error) {
                    throw result.error;
                }
                const orderBatch = result.data?.orders || [];
                setOrders(orderBatch);
                setLoadedSearch(requestedSearch);
                setHasMore(orderBatch.length === INVENTORY_PAGE_SIZE);
            } catch (error) {
                if (requestId === loadOrdersRequestId.current) {
                    setHasMore(false);
                    reportInventoryError(error, 'Inventory could not be loaded. Please try again.');
                }
            } finally {
                if (requestId === loadOrdersRequestId.current) {
                    setInventoryLoading(false);
                }
            }
        },
        [getOrders, reportInventoryError]
    );

    const getOrdersDebounced = useMemo(() => debounce(loadOrders, 500), [loadOrders]);

    const isForwardStocking = primaryTab === PRIMARY_TABS.FORWARD_STOCKING;

    useEffect(() => {
        if (isForwardStocking) {
            setInventoryLoading(false);
            setHasMore(false);
            setOrders([]);
            return () => {};
        }

        const requestId = ++loadOrdersRequestId.current;
        setHasMore(false);
        setInventoryLoading(true);
        const loadOptions = {
            requestId,
            requestedSearch: search,
            queryOptions: {
                variables: {
                    where,
                    limit: INVENTORY_PAGE_SIZE,
                },
            },
        };
        // debounce only covers predicate churn from typing — first load and StrictMode's remount of the same
        // predicate both go straight out
        const isImmediateLoad = lastRequestedWhere.current === null || lastRequestedWhere.current === where;
        lastRequestedWhere.current = where;
        if (isImmediateLoad) {
            loadOrders(loadOptions);
        } else {
            getOrdersDebounced(loadOptions);
        }

        return () => {
            if (loadOrdersRequestId.current === requestId) {
                loadOrdersRequestId.current++;
            }
            getOrdersDebounced.cancel();
        };
    }, [getOrdersDebounced, search, where, isForwardStocking]);

    const cursor = useMemo(() => getInventoryCursor(orders), [orders]);

    const loadMoreInFlight = useRef(false);

    const loadMore = useCallback(() => {
        if (loadMoreInFlight.current) {
            return Promise.resolve();
        }
        // InfiniteLoader won't retry an unchanged range, so every dead end has to clear hasMore itself
        if (!cursor) {
            setHasMore(false);
            return Promise.resolve();
        }
        loadMoreInFlight.current = true;
        setLoadingMore(true);
        const requestId = loadOrdersRequestId.current;

        return getOrders({
            variables: {
                where: {
                    _and: [where, getInventoryCursorWhere(cursor)],
                },
                limit: INVENTORY_PAGE_SIZE,
            },
        })
            .then((result) => {
                if (requestId !== loadOrdersRequestId.current) {
                    return;
                }
                if (result.error) {
                    throw result.error;
                }

                const orderBatch = result.data?.orders || [];
                setOrders((previousOrders) => {
                    const ordersById = new Map(previousOrders.map((order) => [order.order_id, order]));
                    orderBatch.forEach((order) => ordersById.set(order.order_id, order));
                    return Array.from(ordersById.values());
                });
                if (orderBatch.length < INVENTORY_PAGE_SIZE) {
                    setHasMore(false);
                }
            })
            .catch((error) => {
                if (requestId === loadOrdersRequestId.current) {
                    setHasMore(false);
                    reportInventoryError(error, 'Inventory could not be loaded. Please try again.');
                }
            })
            .finally(() => {
                loadMoreInFlight.current = false;
                setLoadingMore(false);
            });
    }, [where, cursor, getOrders, reportInventoryError]);

    const exportCsv = useCallback(
        async (selectedColumns, templateName, exportType) => {
            try {
                const result = await getExportData({
                    variables: {
                        where,
                        limit: INVENTORY_EXPORT_LIMIT,
                    },
                });
                if (result.error) {
                    throw result.error;
                }

                const exportOrders = result.data?.orders || [];
                const exportItemsBySku = processOrders(exportOrders, tabIndex, search);
                const exportPalletsBySku = processPallets(exportItemsBySku, tabIndex);
                const dataToExport = isPalletView ? exportPalletsBySku : exportItemsBySku;

                const rowData = dataToExport.map((item) => {
                    if (isPalletView) {
                        return {
                            sku: item.sku,
                            palletId: item.pallets[0]
                                ? item.pallets[0].pallet_name || item.pallets[0].pallet_number
                                : 'No pallets received yet for sku',
                            order_number: item.order_number,
                            po_number: item.po_number,
                            shipper: item.shipper,
                            qtyIn: item?.individualPalletInfo?.[item.palletId]?.receivedQuantity || 0,
                            qtyOut: item?.individualPalletInfo?.[item.palletId]?.outQuantity || 0,
                            qtyOnHand:
                                item?.individualPalletInfo?.[item.palletId]?.receivedQuantity -
                                (item?.individualPalletInfo?.[item.palletId]?.outQuantity || 0),
                            qtyTotal: item?.individualPalletInfo?.[item.palletId]?.totalQuantity || 0,
                            locations: item.pallets[0]?.warehouse_location || '-',
                            dateIn: item.dateIn,
                            dateOut: item.dateOut,
                            inboundManifests: item.inboundManifests.join(', '),
                            outboundManifests: item.outboundManifests.join(', '),
                            status: PALLET_STATUS_LABELS[item.pallets[0]?.warehouse_status] || 'Not Received',
                            exceptions: item.exceptions,
                        };
                    }

                    return {
                        sku: item.sku,
                        palletId:
                            item.pallets.length > 0
                                ? item.pallets.map((p) => p.pallet_name || p.pallet_number).join(', ')
                                : 'No pallets received yet for sku',
                        order_number: item.order_number,
                        po_number: item.po_number,
                        shipper: item.shipper,
                        qtyIn: item?.receivedQuantity || 0,
                        qtyOut: item?.outQuantity || 0,
                        qtyOnHand: item?.receivedQuantity - item?.outQuantity || 0,
                        qtyTotal: item?.totalQuantity || 0,
                        locations: item.wh_locations.join(', '),
                        dateIn: item.dateIn,
                        dateOut: item.dateOut,
                        inboundManifests: item.inboundManifests.join(', '),
                        outboundManifests: item.outboundManifests.join(', '),
                        status: item.status,
                        exceptions: item.exceptions,
                    };
                });

                if (templateName) {
                    insertTemplate({
                        variables: {
                            object: {
                                name: templateName,
                                user_id: templateOwnerId,
                                export_type: exportType,
                                template: selectedColumns,
                            },
                        },
                    });
                }

                const finalExportCols = INVENTORY_CSV_COLUMNS.filter((col) => selectedColumns['Inventory'][col.header]);

                if (finalExportCols.length > 0) {
                    csvDownload({
                        headers: finalExportCols.map((col) => col.header),
                        data: rowData.map((row) => finalExportCols.map((col) => col.value(row))),
                        filename: `onward-Inventory-Export-${new Date().toISOString()}.csv`,
                        delimiter: ',',
                    });
                }
            } catch (error) {
                reportInventoryError(error, 'Inventory export could not be created. Please try again.');
            }
        },
        [getExportData, insertTemplate, isPalletView, reportInventoryError, search, tabIndex, templateOwnerId, where]
    );

    const callbacks = {
        setTabIndex,
        setPrimaryTab,
        setNotification,
        setModal,
        loadMore,
        exportCsv,
        setSearch,
        setSelectedSku,
        setFilters,
        hasMore,
        setIsPalletView,
    };

    const columns = useTableColumns({
        callbacks,
        isPalletView,
    });

    return (
        <Context.Provider
            value={{
                state: {
                    tabIndex,
                    primaryTab,
                    itemsBySku,
                    palletsBySku,
                    orders,
                    notification,
                    modal,
                    columns,
                    hasMore,
                    search,
                    selectedSku,
                    shipperOptions,
                    filters,
                    isPalletView,
                },
                loading: {
                    init: inventoryLoading,
                    export: exportOrdersLoading,
                    loadingMore,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
